import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Truck, 
  Package, 
  Plus,
  Eye,
  Euro,
  Clock,
  MessageSquare,
  LogOut,
  Search,
  FileText,
  Mail,
  Bell
} from "lucide-react";
import { toast } from "sonner";

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes, unreadRes, supportUnreadRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`, { headers }),
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/messages/unread-count`, { headers }),
        axios.get(`${API}/tickets/unread-replies`, { headers }).catch(() => ({ data: { count: 0 } }))
      ]);
      
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setUnreadCount(unreadRes.data.count);
      setSupportUnreadCount(supportUnreadRes.data.count);
    } catch (e) {
      console.error("Error fetching data:", e);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(o => 
    o.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.receiver_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const statusMap = {
      "PENDING": { label: "Ausstehend", class: "bg-amber-100 text-amber-800" },
      "PICKED_UP": { label: "Abgeholt", class: "bg-blue-100 text-blue-800" },
      "IN_TRANSIT": { label: "In Zustellung", class: "bg-indigo-100 text-indigo-800" },
      "DELIVERED": { label: "Zugestellt", class: "bg-green-100 text-green-800" },
      "CANCELLED": { label: "Storniert", class: "bg-red-100 text-red-800" },
      "CUSTOMER_ABSENT": { label: "Kunde abwesend", class: "bg-red-100 text-red-800" },
      "REDELIVERY": { label: "Erneute Zustellung", class: "bg-orange-100 text-orange-800" }
    };
    const s = statusMap[status] || { label: status, class: "bg-slate-100 text-slate-800" };
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg">SonderCargo.de</span>
                <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-white font-medium" data-testid="nav-dashboard">
                Dashboard
              </Link>
              <Link to="/inbox" className="text-slate-300 hover:text-white transition relative" data-testid="nav-inbox">
                Posteingang
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-4 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Link>
              <Link to="/invoices" className="text-slate-300 hover:text-white transition" data-testid="nav-invoices">
                Rechnungen
              </Link>
              <Link to="/support" className="text-slate-300 hover:text-white transition relative" data-testid="nav-support">
                Support
                {supportUnreadCount > 0 && (
                  <span className="absolute -top-2 -right-4 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {supportUnreadCount}
                  </span>
                )}
              </Link>
              {user?.is_active !== false ? (
                <Link to="/create-order">
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-order-btn">
                    Sendung erstellen
                  </Button>
                </Link>
              ) : (
                <Button 
                  className="bg-slate-400 cursor-not-allowed opacity-60" 
                  disabled 
                  title="Konto wartet auf Aktivierung"
                  data-testid="create-order-btn-disabled"
                >
                  Sendung erstellen
                </Button>
              )}
            </nav>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="font-medium" data-testid="user-name">{user?.name}</p>
                <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
              </div>
              <Link to="/account">
                <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" title="Kontoeinstellungen" data-testid="account-settings-btn">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </Button>
              </Link>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400" />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Activation Warning for Business Customers */}
        {user?.role === "business" && user?.is_active === false && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="activation-warning">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-800">Konto wartet auf Aktivierung</h3>
                <p className="text-amber-700 text-sm mt-1">
                  Ihr Business-Konto wurde erfolgreich erstellt und wird derzeit von unserem Team geprüft. 
                  Sobald Ihr Konto aktiviert ist, können Sie Sendungen erstellen. 
                  Unser Support-Team wird sich in Kürze bei Ihnen melden.
                </p>
                <p className="text-amber-600 text-xs mt-2">
                  Bei Fragen wenden Sie sich bitte an unseren Support.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">
            Willkommen, {user?.name}!
          </h1>
          <p className="text-slate-500">
            {user?.role === "business" ? "Business-Konto" : "Privatkonto"} • 
            {user?.payment_method === "invoice" ? " Zahlung auf Rechnung" : " Sofortzahlung"}
            {user?.flat_rate && ` • Pauschalpreis: ${user.flat_rate.toFixed(2)}€`}
            {user?.role === "business" && user?.is_active === false && (
              <span className="ml-2 text-amber-600 font-medium">• Wartet auf Aktivierung</span>
            )}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm" data-testid="stat-orders">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Meine Sendungen</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_orders || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm" data-testid="stat-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Gesamtausgaben</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_revenue?.toFixed(2) || "0.00"}€</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Euro className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Ausstehend</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.pending_orders || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm" data-testid="stat-transit">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">In Zustellung</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.in_transit || 0}</p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Truck className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {user?.is_active !== false ? (
            <Link to="/create-order">
              <Card className="border-0 shadow-sm hover:shadow-md transition cursor-pointer group" data-testid="quick-create">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center group-hover:bg-blue-700 transition">
                    <Plus className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900">Neue Sendung</h3>
                    <p className="text-slate-500 text-sm">Express-Versand</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ) : (
            <Card className="border-0 shadow-sm opacity-60 cursor-not-allowed" data-testid="quick-create-disabled">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 bg-slate-400 rounded-xl flex items-center justify-center">
                  <Plus className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">Neue Sendung</h3>
                  <p className="text-amber-600 text-sm">Aktivierung ausstehend</p>
                </div>
              </CardContent>
            </Card>
          )}
          
          <Link to="/inbox">
            <Card className="border-0 shadow-sm hover:shadow-md transition cursor-pointer group relative" data-testid="quick-inbox">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center group-hover:bg-amber-600 transition relative">
                  <Mail className="h-7 w-7 text-white" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">Posteingang</h3>
                  <p className="text-slate-500 text-sm">
                    {unreadCount > 0 ? `${unreadCount} neue Nachricht(en)` : "Alle gelesen"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/invoices">
            <Card className="border-0 shadow-sm hover:shadow-md transition cursor-pointer group" data-testid="quick-invoices">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center group-hover:bg-purple-700 transition">
                  <FileText className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">Rechnungen</h3>
                  <p className="text-slate-500 text-sm">
                    {user?.role === "business" ? "Monatliche Abrechnung" : "Zahlungsübersicht"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/support">
            <Card className="border-0 shadow-sm hover:shadow-md transition cursor-pointer group" data-testid="quick-support">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 bg-slate-800 rounded-xl flex items-center justify-center group-hover:bg-slate-900 transition relative">
                  <MessageSquare className="h-7 w-7 text-white" />
                  {supportUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {supportUnreadCount}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-900">Support</h3>
                  <p className="text-slate-500 text-sm">
                    {supportUnreadCount > 0 ? `${supportUnreadCount} neue Antwort(en)` : "Hilfe & Tickets"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Orders */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle data-testid="orders-title">Meine Sendungen</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Suchen..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="order-search"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="orders-table">
                <thead>
                  <tr className="border-b text-left bg-slate-50">
                    <th className="p-4 text-xs font-medium text-slate-500 uppercase">Sendungsnummer</th>
                    <th className="p-4 text-xs font-medium text-slate-500 uppercase">Empfänger</th>
                    <th className="p-4 text-xs font-medium text-slate-500 uppercase">Datum</th>
                    <th className="p-4 text-xs font-medium text-slate-500 uppercase">Preis</th>
                    <th className="p-4 text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="p-4 text-xs font-medium text-slate-500 uppercase">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map(order => (
                    <tr key={order.id} className="border-b hover:bg-slate-50" data-testid={`order-row-${order.id}`}>
                      <td className="p-4 font-medium">{order.order_number}</td>
                      <td className="p-4">{order.receiver_name}</td>
                      <td className="p-4 text-slate-600">{order.created_at?.split("T")[0]}</td>
                      <td className="p-4 font-medium">
                        {(order.price_override || order.total_price).toFixed(2)}€
                      </td>
                      <td className="p-4">{getStatusBadge(order.status)}</td>
                      <td className="p-4">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => navigate(`/order/${order.id}`)}
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500">
                        {orders.length === 0 ? (
                          <div>
                            <Package className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                            <p>Noch keine Sendungen</p>
                            <Link to="/create-order">
                              <Button className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="empty-create">
                                Erste Sendung erstellen
                              </Button>
                            </Link>
                          </div>
                        ) : (
                          "Keine Ergebnisse gefunden"
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
